import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { requireAccessToken } from '../utils/authToken';
import HoleByHoleGrid, { ActiveCell, CellState, GridRow } from '../components/HoleByHoleGrid';
import ScorecardPhoto from '../components/ScorecardPhoto';
import ScorecardTasks, { PlayerPick } from '../components/ScorecardTasks';
import { CellValue } from '../components/CellEditor';
import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery';
import { shrinkImage } from '../utils/shrinkImage';
import {
  DraftEdit,
  DraftRow,
  Flag,
  ReadProgress,
  ReviewState,
  ViewRow,
  scorecardApi,
  statusLabel,
} from '../utils/scorecardApi';
import '../css/scorecard.css';

type Tab = 'chat' | 'scores';
type PhotoSize = 'sm' | 'md' | 'lg';

const LOW_CONFIDENCE = 0.8;
const WORKING = ['reading', 'agent_working'];
// Flags about who a row is — step 1, not a hole to fix.
const PLAYER_FLAGS = new Set(['needs_player', 'duplicate_player', 'not_in_round']);
const NINE_FLAGS = new Set(['nine_mismatch', 'unverified_nine']);
const nineOf = (hole: number) => (hole <= 9 ? 'front' : 'back');
const PHOTO_SIZES: PhotoSize[] = ['sm', 'md', 'lg'];

const fmtDate = (d?: string | null) => {
  if (!d) return '';
  const dt = new Date(`${d.slice(0, 10)}T12:00:00`);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const fmtWhen = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Wide enough for the photo and the side column (To check + chat) next to each other. */
export const WIDE_QUERY = '(min-width: 1100px)';

const mmss = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** "Claude is studying the card… 1:30" — from the server's last heartbeat, ticking locally. */
const ReadingProgress: React.FC<{ progress?: ReadProgress | null }> = ({ progress }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const since = progress?.at ? Math.max(0, now - Date.parse(progress.at)) : 0;
  const elapsed = (progress?.elapsed_ms || 0) + since;
  const phase = progress?.phase || 'waiting';
  const twice = (progress?.passes || 1) > 1 ? ' (two independent reads)' : '';
  const text = phase === 'thinking' ? `Claude is studying the card${twice}…`
    : phase === 'writing' ? `Writing the scores…${progress?.holes_written ? ` ${progress.holes_written} holes so far` : ''}`
    : 'Waiting for Claude to start…';
  // As of the last heartbeat: how much Claude has thought, and whether data is still arriving.
  const quietMs = progress?.last_event_ms != null ? (progress.elapsed_ms || 0) - progress.last_event_ms : 0;
  const notes = [
    progress?.thinking_chars ? `${progress.thinking_chars.toLocaleString('en-US')} characters of notes so far` : null,
    quietMs >= 60000 ? `no data from Claude for ${mmss(quietMs)}` : null,
    progress?.paused_ms && progress.paused_ms >= 30000 ? `the server was asleep for ${mmss(progress.paused_ms)}` : null,
  ].filter(Boolean);
  return (
    <div role="status">
      <p style={{ margin: '0 0 6px', fontWeight: 700 }}>📖 {text} <span className="sc-note">{mmss(elapsed)}</span></p>
      {notes.length > 0 && <p className="sc-note" style={{ margin: '0 0 6px' }}>{notes.join(' · ')}</p>}
      <p className="sc-note" style={{ margin: 0 }}>
        A busy card can take a few minutes{progress?.cap_ms ? ` (it's stopped after ${Math.round(progress.cap_ms / 60000)} min)` : ''}.
        You can leave this page — it'll be in My Career when it's ready.
      </p>
    </div>
  );
};

/**
 * Review a scorecard photo before its hole-by-hole scores are saved to the
 * round: the photo, what still needs checking (with one-tap fixes), who each
 * row is, the scores (tap a hole to fix it), and a chat with the review
 * assistant. Nothing is saved until "Save to round" — and that stays off
 * while anything is blocking.
 *
 * Phones (and tablets) get a full-screen layout built around the photo: it
 * stays pinned at the top (at the side in landscape) while the reviewer works
 * below it in two tabs — Chat (opens first: the steps left — who's who, then
 * the holes, then save — and the assistant) and Scores (problem holes in red;
 * tapping one opens its editor under the photo with what's wrong with it).
 */
const ScorecardReview: React.FC = () => {
  const { uploadId: idParam } = useParams();
  const uploadId = Number(idParam);
  const { getAccessTokenSilently } = useAuth0();
  const getToken = useMemo(() => requireAccessToken(getAccessTokenSilently), [getAccessTokenSilently]);
  const isMobile = useIsMobile();
  const isWide = useMediaQuery(WIDE_QUERY) && !isMobile;

  const [state, setState] = useState<ReviewState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveCell | null>(null);
  // Which hole's editor is open (the page opens problem holes itself).
  const [editing, setEditing] = useState<ActiveCell | null>(null);
  const [tab, setTab] = useState<Tab>('chat');
  const [photoSize, setPhotoSize] = useState<PhotoSize>('md');
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const [saved, setSaved] = useState<{ names: string[]; aliases: number } | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const retakeRef = useRef<HTMLInputElement>(null);
  const chatLogRef = useRef<HTMLDivElement>(null);
  const playersRef = useRef<HTMLDivElement>(null);
  const scoresRef = useRef<HTMLDivElement>(null);
  const photoPaneRef = useRef<HTMLDivElement>(null);
  const mobileBodyRef = useRef<HTMLDivElement>(null);
  const seenMessages = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await scorecardApi.get(getToken, uploadId);
      setState(s);
      setLoadError(null);
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't load this scorecard.");
    }
  }, [getToken, uploadId]);

  useEffect(() => { load(); }, [load]);

  // Poll while Claude is reading the card or the assistant is replying.
  const status = state?.upload.status;
  useEffect(() => {
    if (!status || !WORKING.includes(status)) return undefined;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [status, state, load]);

  const messageCount = state?.messages.length ?? 0;
  // Keep the newest message in view — scrolling only the chat, never the page
  // (the reviewer may be working in the score grid further down).
  useEffect(() => {
    const log = chatLogRef.current;
    if (log) log.scrollTop = log.scrollHeight;
    // Phones: the steps sit above the messages — scroll down only when a new
    // message arrives, so the steps are what the reviewer sees first.
    const body = mobileBodyRef.current;
    if (body && seenMessages.current != null && messageCount > seenMessages.current) body.scrollTop = body.scrollHeight;
    if (state) seenMessages.current = messageCount;
  }, [messageCount, tab, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const draft = state?.draft ?? null;
  const view = state?.view ?? null;
  const canEdit = !!state?.can_review && !!status && ['needs_review', 'ready', 'agent_working'].includes(status);
  const playersById = useMemo(() => new Map((state?.players || []).map((p) => [p.player_id, p])), [state]);
  // Phones & tablets, once the card is read: the full-screen review.
  const mobileReview = isMobile && !!state?.draft && !!status && ['needs_review', 'ready', 'agent_working', 'committed'].includes(status);
  useEffect(() => {
    if (!mobileReview) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // the page behind mustn't scroll under the full-screen view
    return () => { document.body.style.overflow = prev; };
  }, [mobileReview]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err?.message || 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  const edit = (edits: DraftEdit[]) => run('edit', async () => {
    const r = await scorecardApi.edit(getToken, uploadId, edits);
    setState((s) => (s ? { ...s, draft: r.draft, view: r.view } : s));
    load(); // picks up the edit note in the chat + status
  });

  const rowView = (rowId: string): ViewRow | undefined => view?.rows.find((r) => r.row_id === rowId);

  const rowLabel = (row: DraftRow) => {
    const p = row.assignment.player_id != null ? playersById.get(row.assignment.player_id) : null;
    if (p && row.assignment.status !== 'skipped') return `${p.first_name} ${p.last_name}`;
    return row.name_text ? `“${row.name_text}”` : `Row ${row.row_id.slice(1)}`;
  };

  // What needs fixing in the scores: blocking flags that point at holes
  // (red cells), at a whole nine (its OUT / IN in red), or at neither (a row
  // check such as the gross — listed as a step in the chat).
  const problems = useMemo(() => {
    const byCell = new Map<string, Flag[]>();
    const byNine = new Map<string, Flag[]>();
    const other: Flag[] = [];
    const put = (m: Map<string, Flag[]>, k: string, f: Flag) => m.set(k, [...(m.get(k) || []), f]);
    for (const f of view?.flags || []) {
      if (!f.blocking || PLAYER_FLAGS.has(f.type)) continue;
      const holes = f.hole ? [f.hole] : f.holes || [];
      if (f.row_id && holes.length) holes.forEach((h) => put(byCell, `${f.row_id}:${h}`, f));
      else if (f.row_id && NINE_FLAGS.has(f.type)) put(byNine, `${f.row_id}:${f.id.endsWith(':back') ? 'back' : 'front'}`, f);
      else other.push(f);
    }
    // In card order: row by row, hole by hole; a nine with no hole named starts at its first hole.
    const cells: ActiveCell[] = [];
    for (const row of draft?.rows || []) {
      for (const h of draft!.played_holes) {
        const nineKey = `${row.row_id}:${nineOf(h)}`;
        const firstOfNine = h === draft!.played_holes.find((x) => nineOf(x) === nineOf(h));
        const nineHasCell = draft!.played_holes.some((x) => nineOf(x) === nineOf(h) && byCell.has(`${row.row_id}:${x}`));
        if (byCell.has(`${row.row_id}:${h}`) || (firstOfNine && byNine.has(nineKey) && !nineHasCell)) cells.push({ rowKey: row.row_id, hole: h });
      }
    }
    const flagsAt = (rowKey: string, hole: number) => [...(byCell.get(`${rowKey}:${hole}`) || []), ...(byNine.get(`${rowKey}:${nineOf(hole)}`) || [])];
    return { byCell, byNine, other, cells, flagsAt };
  }, [view, draft]);

  const nextProblem = (after?: ActiveCell | null): ActiveCell | null => {
    const list = problems.cells.filter((c) => !after || c.rowKey !== after.rowKey || c.hole !== after.hole);
    if (!list.length) return null;
    if (!after) return list[0];
    const order = (c: ActiveCell) => (draft?.rows.findIndex((r) => r.row_id === c.rowKey) ?? 0) * 100 + c.hole;
    return list.find((c) => order(c) > order(after)) || list[0];
  };

  const gridRows: GridRow[] = useMemo(() => {
    if (!draft || !view) return [];
    return draft.rows.map((row) => {
      const v = rowView(row.row_id)!;
      const cellState: Record<number, CellState[]> = {};
      const add = (h: number, s: CellState) => { cellState[h] = [...(cellState[h] || []), s]; };
      for (const c of row.cells) {
        const nine = c.hole <= 9 ? 'front' : 'back';
        const verified = v.nines[nine]?.balanced === true;
        if (c.source === 'read' && c.kind !== 'blank' && c.confidence < LOW_CONFIDENCE && !verified) add(c.hole, 'low');
        if (c.source !== 'read') add(c.hole, 'edited');
      }
      v.resolved_holes.forEach((h) => add(h, 'resolved'));
      for (const h of draft.played_holes) if (problems.byCell.has(`${row.row_id}:${h}`)) add(h, 'flag');
      const w = row.written;
      const written = [w.out?.value, w.in?.value, w.total?.value].map((x) => (x == null ? '–' : x)).join('/');
      return {
        key: row.row_id,
        label: rowLabel(row),
        subtitle: `“${row.name_text ?? '?'}” · card ${written}`,
        strokes: v.strokes,
        putts: draft.include_putts ? v.putts : undefined,
        penalties: draft.include_penalties ? v.penalties : undefined,
        cellState,
        sumFlags: { out: problems.byNine.has(`${row.row_id}:front`), in: problems.byNine.has(`${row.row_id}:back`) },
        muted: !v.eligible && (row.assignment.status === 'skipped' || (row.assignment.player_id != null && !v.in_round)),
        editable: canEdit && row.assignment.status !== 'skipped',
      };
    });
  }, [draft, view, canEdit, playersById, problems]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCellChange = (rowKey: string, hole: number, value: CellValue) => {
    const v = rowView(rowKey);
    if (!v) return;
    const e: DraftEdit & { type: 'set_hole' } = { type: 'set_hole', row_id: rowKey, hole };
    if (value.strokes !== v.strokes[hole - 1]) e.strokes = value.strokes;
    if (draft?.include_putts && value.putts !== v.putts[hole - 1]) e.putts = value.putts;
    if (draft?.include_penalties && value.penalties !== v.penalties[hole - 1]) e.penalties = value.penalties;
    if (e.strokes === undefined && e.putts === undefined && e.penalties === undefined) return;
    edit([e]);
  };

  const showFlag = (f: Flag) => {
    if (f.type === 'needs_player') {
      if (isMobile) { setTab('chat'); mobileBodyRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' }); }
      playersRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }
    const hole = f.hole ?? f.holes?.[0];
    if (f.row_id && hole) {
      setActive({ rowKey: f.row_id, hole });
      if (isMobile) { setTab('scores'); setEditing({ rowKey: f.row_id, hole }); }
      else scoresRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  };

  // Phones: work through the red holes one by one.
  const fixHoles = () => {
    setTab('scores');
    setEditing(nextProblem(null));
  };

  // The editor for a hole shows what's wrong with it; "Looks right" confirms it and moves on.
  const issuesFor = (rowKey: string, hole: number): React.ReactNode => {
    const flags = problems.flagsAt(rowKey, hole);
    if (!flags.length) return null;
    return (
      <ul className="sc-issues">
        {flags.map((f) => (
          <li key={f.id} className="sc-issue">
            <span>{f.message}</span>
            {f.ackable && !f.acked && canEdit && (
              <button
                type="button"
                className="sc-btn sc-btn--sm sc-btn--primary"
                disabled={!!busy}
                onClick={() => { setEditing(nextProblem({ rowKey, hole })); edit([{ type: 'ack', flag_id: f.id }]); }}
              >
                Looks right
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  // On phones the editor opens under (portrait) or beside (landscape) the photo, so the card stays in view.
  const editorArea = () => {
    const el = photoPaneRef.current;
    if (!isMobile || !el) return null;
    const r = el.getBoundingClientRect();
    return r.width >= window.innerWidth - 2 ? { top: Math.ceil(r.bottom) } : { left: Math.ceil(r.right) };
  };

  const flagActions = (f: Flag) => {
    const acts: React.ReactNode[] = [];
    const btn = (label: string, onClick: () => void, primary = false) => (
      <button key={label} type="button" className={`sc-btn sc-btn--sm${primary ? ' sc-btn--primary' : ''}`} disabled={!canEdit || !!busy} onClick={onClick}>{label}</button>
    );
    if ((f.row_id && (f.hole || f.holes?.length)) || f.type === 'needs_player') acts.push(btn(f.type === 'needs_player' ? 'Choose' : 'Show', () => showFlag(f)));
    if (f.type === 'gross_mismatch' && f.row_id) {
      const row = draft?.rows.find((r) => r.row_id === f.row_id);
      acts.push(row?.update_gross
        ? btn("Keep the round's gross", () => edit([{ type: 'row_options', row_id: f.row_id!, update_gross: false }]))
        : btn('Use the card total', () => edit([{ type: 'row_options', row_id: f.row_id!, update_gross: true }]), true));
    }
    if (f.type === 'existing_holes' && f.row_id) acts.push(btn('Replace their scores', () => edit([{ type: 'row_options', row_id: f.row_id!, replace_existing: true }]), true));
    if (f.ackable && !f.acked) acts.push(btn('Looks right', () => edit([{ type: 'ack', flag_id: f.id }]), true));
    if (f.ackable && f.acked) acts.push(btn('Undo', () => edit([{ type: 'ack', flag_id: f.id, value: false }])));
    return acts;
  };

  const sendChat = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    run('chat', async () => {
      await scorecardApi.chat(getToken, uploadId, text);
      setChatText('');
      setState((s) => (s ? {
        ...s,
        upload: { ...s.upload, status: 'agent_working' },
        messages: [...s.messages, { message_id: -Date.now(), role: 'user', kind: 'chat', display_text: text, created_at: new Date().toISOString() }],
      } : s));
    });
  };

  const commit = () => {
    if (!draft || !view) return;
    const grossRows = draft.rows.filter((r) => r.update_gross && view.flags.some((f) => f.type === 'gross_mismatch' && f.row_id === r.row_id));
    if (grossRows.length && !window.confirm(`This also changes the round's gross score for ${grossRows.map(rowLabel).join(', ')} (net is recalculated). Handicaps already stored on later rounds are not recalculated. Save?`)) return;
    run('commit', async () => {
      const r = await scorecardApi.commit(getToken, uploadId);
      setSaved({ names: r.saved.map((x) => x.name), aliases: r.aliases_learned.length });
      await load();
    });
  };

  const discard = () => {
    if (!window.confirm('Discard this scorecard? Nothing from it will be saved.')) return;
    run('discard', async () => { await scorecardApi.discard(getToken, uploadId); await load(); });
  };

  const reread = (file?: File) => run('reread', async () => {
    let image: Blob | undefined;
    let meta = {};
    if (file) {
      const shrunk = await shrinkImage(file);
      image = shrunk.blob;
      meta = { filename: file.name, width: shrunk.width, height: shrunk.height };
    }
    await scorecardApi.reread(getToken, uploadId, image, meta);
    if (image) setPhotoVersion((v) => v + 1);
    setSaved(null);
    await load();
  });

  if (loadError && !state) {
    return <div className="bga-page"><p className="sc-error">{loadError}</p><Link to="/rounds">← Back to rounds</Link></div>;
  }
  if (!state) return <div className="bga-page"><p className="sc-note">Loading…</p></div>;

  const round = state.round;
  const blocking = view ? view.flags.filter((f) => f.blocking) : [];
  const notes = view ? view.flags.filter((f) => !f.blocking) : [];
  const working = status === 'agent_working';
  // "Who's who" goes above the scores only while someone still needs matching.
  const playersFirst = blocking.some((f) => f.type === 'needs_player' || f.type === 'duplicate_player');

  // ── Panels ──
  // What the photo is, so the reviewer can tell it's the one they took.
  const up = state.upload;
  const photoCaption = (
    <>
      {up.filename ? <b>{up.filename}</b> : 'Photo'}
      {up.width && up.height ? ` · ${up.width} × ${up.height}` : ''}
      {up.usage?.photo?.replaced_at ? ` · replaced ${fmtWhen(up.usage.photo.replaced_at)}` : ` · uploaded ${fmtWhen(up.created_at)}`}
      {state.uploader_name ? ` by ${state.uploader_name}` : ''}
    </>
  );
  const photo = (
    <ScorecardPhoto
      uploadId={uploadId}
      getToken={getToken}
      height={isMobile ? '62vh' : undefined /* sized by the layout's CSS */}
      version={photoVersion}
      caption={photoCaption}
    />
  );

  const chatMessages = (
    <>
      {state.messages.length === 0 && !working && <p className="sc-note">The review assistant's notes will appear here.</p>}
      {state.messages.map((m) => (
        m.kind === 'edit_event' || m.kind === 'system'
          ? <div key={m.message_id} className="sc-chat-event">{m.kind === 'edit_event' ? `✎ ${m.display_text}` : m.display_text}</div>
          : <div key={m.message_id} className={`sc-bubble sc-bubble--${m.role}`}>{m.display_text}</div>
      ))}
      {(working || status === 'reading') && <div className="sc-bubble sc-bubble--assistant sc-typing">{status === 'reading' ? 'Reading the card…' : 'Checking…'}</div>}
    </>
  );
  const chatForm = state.can_review && status !== 'committed' && status !== 'discarded' && (
    <form className="sc-chat-form" onSubmit={sendChat}>
      <textarea
        value={chatText}
        onChange={(e) => setChatText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) sendChat(e); }}
        placeholder={working ? 'The assistant is replying…' : isMobile ? 'e.g. "Nick had a 9 on 18"' : 'e.g. "Nick had a 9 on 18" or "Eliot is Elias"'}
        aria-label="Message the review assistant"
        rows={isMobile ? 1 : 2}
        disabled={working || status === 'reading' || status === 'failed'}
      />
      <button type="submit" className="sc-btn sc-btn--primary" disabled={!chatText.trim() || working || busy === 'chat' || status === 'reading'}>Send</button>
    </form>
  );
  const chat = (
    <div className="sc-chat">
      <div className="sc-chat-log" aria-live="polite" ref={chatLogRef}>{chatMessages}</div>
      {chatForm}
    </div>
  );

  const flagsPanel = view && (
    <div className="sc-panel">
      {blocking.length > 0 ? (
        <>
          <h3 className="sc-panel-title">To check <span className="sc-count">{blocking.length}</span></h3>
          <ul className="sc-flags">
            {blocking.map((f) => (
              <li key={f.id} className={`sc-flag sc-flag--${f.severity}`}>
                <span className="sc-flag-msg">{f.message}</span>
                <span className="sc-flag-actions">{flagActions(f)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="sc-ok" style={{ margin: 0 }}>✓ Everything checks out{view.eligible_count ? ` — ${view.eligible_count} player${view.eligible_count === 1 ? '' : 's'} ready to save.` : '.'}</p>
      )}
      {notes.length > 0 && (
        <>
          <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm" onClick={() => setShowNotes((v) => !v)} style={{ marginTop: 8 }}>
            {showNotes ? '▾' : '▸'} Notes ({notes.length})
          </button>
          {showNotes && (
            <ul className="sc-flags">
              {notes.map((f) => (
                <li key={f.id} className={`sc-flag sc-flag--${f.acked ? 'info' : f.severity}`}>
                  <span className="sc-flag-msg">{f.acked ? `✓ ${f.message}` : f.message}</span>
                  <span className="sc-flag-actions">{flagActions(f)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );

  const playersPanel = draft && view && (
    <div className="sc-panel" ref={playersRef}>
      <h3 className="sc-panel-title">Who's who</h3>
      {draft.rows.map((row) => (
        <PlayerPick
          key={row.row_id}
          row={row}
          total={rowView(row.row_id)?.total}
          roundPlayers={state.round?.players || []}
          players={state.players}
          canEdit={canEdit}
          busy={!!busy}
          onEdit={edit}
        />
      ))}
    </div>
  );

  const scoresPanel = draft && view && (
    <div className="sc-panel" ref={scoresRef}>
      <div className="sc-toolbar" style={{ marginTop: 0 }}>
        <h3 className="sc-panel-title" style={{ margin: 0 }}>Scores</h3>
        <span className="sc-note">{canEdit ? 'Tap a hole to fix it.' : ''}</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="sc-note"><input type="checkbox" checked={draft.include_putts} disabled={!canEdit || !!busy} onChange={(e) => edit([{ type: 'include', putts: e.target.checked }])} /> Putts</label>
          <label className="sc-note"><input type="checkbox" checked={draft.include_penalties} disabled={!canEdit || !!busy} onChange={(e) => edit([{ type: 'include', penalties: e.target.checked }])} /> Penalties</label>
        </span>
      </div>
      <HoleByHoleGrid
        pars={draft.pars}
        playedHoles={draft.played_holes}
        rows={gridRows}
        editable={canEdit}
        showExtras={draft.include_putts || draft.include_penalties}
        onCellChange={onCellChange}
        onCellSelect={(rowKey, hole) => setActive({ rowKey, hole })}
        activeCell={active}
        editing={editing}
        onEditingChange={setEditing}
        issuesFor={issuesFor}
        nextCell={problems.cells.length ? (rowKey, hole) => nextProblem({ rowKey, hole }) : undefined}
        nextLabel={problems.cells.length ? 'Next problem →' : undefined}
        editorArea={editorArea}
      />
      <div className="sc-legend" style={{ marginTop: 8 }}>
        <span><span className="sc-legend-swatch sc-legend-swatch--flag" /> needs a look</span>
        <span><span className="sc-legend-swatch" style={{ background: '#fef3c7' }} /> unsure</span>
        <span><span className="sc-legend-swatch" style={{ background: '#e0f2fe' }} /> fixed by the card's total</span>
        <span><span className="sc-legend-swatch sc-legend-dot" /> changed in review</span>
      </div>
    </div>
  );

  const saveBar = view && canEdit && !(isMobile && tab === 'chat') && (
    <div className="sc-savebar">
      <span className={view.can_commit ? 'sc-ok' : 'sc-note'}>
        {view.can_commit ? '✓ Ready to save' : `${view.blocking_count} thing${view.blocking_count === 1 ? '' : 's'} to check`}
      </span>
      <span className="grow" />
      <button type="button" className="sc-btn sc-btn--sm sc-btn--ghost sc-btn--danger" onClick={discard} disabled={!!busy}>Discard</button>
      <button type="button" className="sc-btn sc-btn--primary" onClick={commit} disabled={!view.can_commit || !!busy || working}>
        {busy === 'commit' ? 'Saving…' : 'Save to round'}
      </button>
    </div>
  );

  const header = (
    <div className="sc-review-head">
      <div>
        <Link to="/rounds" className="sc-note">← Rounds</Link>
        <h1 className="bga-h1" style={{ fontSize: 22, marginTop: 4 }}>Scorecard review</h1>
        <p className="bga-sub" style={{ marginTop: 2 }}>
          {round ? `${round.course_name} · ${fmtDate(round.date)}${round.teebox_name ? ` · ${round.teebox_name} tees` : ''}` : ''}
          {state.uploader_name ? ` · photo from ${state.uploader_name}` : ''}
        </p>
      </div>
      <div className="sc-review-head-actions">
        {status && (
          <span className={`sc-chip-status sc-chip-status--${status === 'ready' || status === 'committed' ? 'ready' : WORKING.includes(status) ? 'reading' : status === 'failed' ? 'failed' : 'review'}`}>
            {statusLabel(status)}
          </span>
        )}
        {state.can_review && status && ['needs_review', 'ready', 'failed'].includes(status) && (
          <>
            <button type="button" className="sc-btn sc-btn--sm" disabled={!!busy} onClick={() => reread()}>Read again</button>
            <button type="button" className="sc-btn sc-btn--sm" disabled={!!busy} onClick={() => retakeRef.current?.click()}>New photo</button>
            <input ref={retakeRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) reread(f); }} />
          </>
        )}
      </div>
    </div>
  );

  // ── Phones & tablets: the full-screen review ──
  if (mobileReview && draft && view) {
    const playersLeft = draft.rows.filter((r) => !['auto', 'confirmed', 'skipped'].includes(r.assignment.status)).length;
    const holesToFix = problems.cells.length;
    const chip = status === 'ready' || status === 'committed' ? 'ready' : working ? 'reading' : 'review';
    return (
      <div className={`sc-m sc-m--photo-${photoSize}`}>
        <header className="sc-m-head">
          <Link to="/rounds" className="sc-m-back" aria-label="Back to rounds">←</Link>
          <div className="sc-m-title">
            <strong>{round?.course_name || 'Scorecard review'}</strong>
            <span>{round ? fmtDate(round.date) : ''}{state.uploader_name ? ` · ${state.uploader_name}` : ''}</span>
          </div>
          {status && <span className={`sc-chip-status sc-chip-status--${chip}`}>{statusLabel(status)}</span>}
          <div className="sc-m-menu">
            <button type="button" className="sc-m-iconbtn" aria-label="More" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>⋯</button>
            {menuOpen && (
              <>
                <div className="sc-m-menu-scrim" onClick={() => setMenuOpen(false)} />
                <div className="sc-m-menu-list" role="menu">
                  <p className="sc-m-menu-info">{photoCaption}</p>
                  {state.can_review && status && ['needs_review', 'ready'].includes(status) && (
                    <>
                      <button type="button" role="menuitem" disabled={!!busy} onClick={() => { setMenuOpen(false); reread(); }}>Read the card again</button>
                      <button type="button" role="menuitem" disabled={!!busy} onClick={() => { setMenuOpen(false); retakeRef.current?.click(); }}>Use a new photo</button>
                    </>
                  )}
                  {canEdit && <button type="button" role="menuitem" className="sc-m-danger" disabled={!!busy} onClick={() => { setMenuOpen(false); discard(); }}>Discard this scorecard</button>}
                </div>
              </>
            )}
            <input ref={retakeRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) reread(f); }} />
          </div>
        </header>

        {/* Always on screen: the card, to compare with while fixing. */}
        <div className="sc-m-photo" ref={photoPaneRef}>
          <ScorecardPhoto uploadId={uploadId} getToken={getToken} height="100%" version={photoVersion} />
          <button
            type="button"
            className="sc-m-photo-size"
            aria-label={photoSize === 'lg' ? 'Make the photo smaller' : 'Make the photo bigger'}
            onClick={() => setPhotoSize((s) => PHOTO_SIZES[(PHOTO_SIZES.indexOf(s) + 1) % PHOTO_SIZES.length])}
          >
            {photoSize === 'lg' ? '▴ Smaller' : '▾ Bigger'}
          </button>
        </div>

        <div className="sc-tabs sc-m-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'chat'} onClick={() => setTab('chat')}>
            Chat{working ? ' …' : ''}{playersLeft ? <span className="sc-count">{playersLeft}</span> : null}
          </button>
          <button role="tab" aria-selected={tab === 'scores'} onClick={() => setTab('scores')}>
            Scores{holesToFix ? <span className="sc-count sc-count--red">{holesToFix}</span> : null}
          </button>
        </div>

        <div className="sc-m-body" ref={mobileBodyRef}>
          {status === 'committed' && (
            <div className="sc-banner sc-banner--ok">
              ✓ Saved to the round{saved ? ` — ${saved.names.join(', ')}` : ''}. <Link to="/rounds">Back to rounds</Link>
            </div>
          )}
          {!state.can_review && status !== 'committed' && (
            <div className="sc-banner">Only {state.reviewer_name || 'the person who uploaded it'} or the league admin can review this scorecard.</div>
          )}
          {actionError && <div className="sc-banner sc-banner--error" role="alert">{actionError}</div>}
          {tab === 'chat' ? (
            <>
              {status !== 'committed' && (
                <div ref={playersRef}>
                  <ScorecardTasks
                    state={state}
                    view={view}
                    canEdit={canEdit}
                    busy={!!busy}
                    onEdit={edit}
                    problemHoles={holesToFix}
                    onFixHoles={fixHoles}
                    otherFlags={problems.other}
                    flagActions={flagActions}
                    onCommit={commit}
                    committing={busy === 'commit'}
                  />
                </div>
              )}
              <div className="sc-m-chat" aria-live="polite">{chatMessages}</div>
            </>
          ) : scoresPanel}
        </div>

        <footer className="sc-m-foot">
          {tab === 'chat' ? chatForm : canEdit && (
            <div className="sc-m-save">
              <span className={view.can_commit ? 'sc-ok' : 'sc-note'}>
                {view.can_commit ? '✓ Ready to save' : holesToFix ? `${holesToFix} hole${holesToFix === 1 ? '' : 's'} to fix` : `${view.blocking_count} thing${view.blocking_count === 1 ? '' : 's'} to check`}
              </span>
              {holesToFix > 0 && <button type="button" className="sc-btn sc-btn--sm" onClick={fixHoles}>Fix next</button>}
              <button type="button" className="sc-btn sc-btn--primary" onClick={commit} disabled={!view.can_commit || !!busy || working}>
                {busy === 'commit' ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </footer>
      </div>
    );
  }

  let body: React.ReactNode;
  if (status === 'reading') {
    body = (
      <div className="sc-review-grid">
        <div>{photo}</div>
        <div className="sc-panel"><ReadingProgress progress={state.upload.usage?.read?.progress} /></div>
      </div>
    );
  } else if (status === 'failed') {
    body = (
      <div className="sc-review-grid">
        <div>{photo}</div>
        <div className="sc-panel">
          <p className="sc-error">Couldn't read this card. {state.upload.error || 'Unknown error.'}</p>
          <p className="sc-note">Try again, or take a new photo: flat, in good light, the whole card in the frame.</p>
          {state.upload.usage?.read?.summary && (
            <details className="sc-note" style={{ margin: '0 0 10px' }}>
              <summary>Technical details</summary>
              <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>{state.upload.usage.read.summary}</code>
            </details>
          )}
          {state.can_review && <button type="button" className="sc-btn sc-btn--sm sc-btn--danger" onClick={discard} disabled={!!busy}>Discard</button>}
        </div>
      </div>
    );
  } else if (status === 'discarded') {
    body = <div className="sc-panel"><p className="sc-note">This scorecard was discarded — nothing from it was saved.</p></div>;
  } else if (isMobile) {
    // Before the card is read (or when reading failed) phones use the stacked page.
    body = <div className="sc-review-stack">{photo}{flagsPanel}{playersPanel}{scoresPanel}{chat}</div>;
  } else if (isWide) {
    // The photo sits right above the full-width score grid, so a hole can be
    // compared with the card; "To check" and the chat share the space beside
    // the photo. Nothing is pinned, so nothing can slide over the grid.
    body = (
      <>
        <div className="sc-review-top">
          {photo}
          <aside className="sc-review-aside" aria-label="To check and chat">
            {flagsPanel}
            {chat}
          </aside>
        </div>
        {/* Always straight under the photo here; "Who's who" follows (the "Choose" button on a flag jumps to it). */}
        {scoresPanel}
        {playersPanel}
      </>
    );
  } else {
    // Tablet / narrow desktop window: one column.
    body = (
      <div className="sc-review-stack">
        {photo}
        {flagsPanel}
        {playersFirst ? <>{playersPanel}{scoresPanel}</> : <>{scoresPanel}{playersPanel}</>}
        {chat}
      </div>
    );
  }

  return (
    <div className="bga-page sc-review">
      {header}
      {status === 'committed' && (
        <div className="sc-banner sc-banner--ok">
          ✓ Saved to the round{saved ? ` — ${saved.names.join(', ')}` : ''}.
          {saved && saved.aliases > 0 && ` Learned ${saved.aliases} name${saved.aliases === 1 ? '' : 's'} for next time.`}
          {' '}<Link to="/rounds">Back to rounds</Link>
        </div>
      )}
      {!state.can_review && status !== 'committed' && (
        <div className="sc-banner">Only {state.reviewer_name || 'the person who uploaded it'} or the league admin can review this scorecard.</div>
      )}
      {actionError && <div className="sc-banner sc-banner--error" role="alert">{actionError}</div>}
      {body}
      {saveBar}
    </div>
  );
};

export default ScorecardReview;
